/**
 * Seasonal Rankings System
 * Provides time-based leaderboard periods and reward tiers
 */

export interface Season {
  number: number;
  name: string;
  startDate: Date;
  endDate: Date;
  year: number;
}

export interface RewardTier {
  name: string;
  rank: string;
  icon: string;
  coins: number;
  gems: number;
  color: string;
}

export type LeaderboardPeriod = 'all-time' | 'season' | 'monthly' | 'weekly';

const SEASON_NAMES = ['Winter', 'Spring', 'Summer', 'Fall'];

/**
 * Get the current season based on date
 */
export function getCurrentSeason(date: Date = new Date()): Season {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-11
  
  // Seasons: Winter (Dec-Feb), Spring (Mar-May), Summer (Jun-Aug), Fall (Sep-Nov)
  let seasonIndex: number;
  let startMonth: number;
  let endMonth: number;
  let seasonYear = year;
  
  if (month >= 11 || month <= 1) { // Dec, Jan, Feb
    seasonIndex = 0; // Winter
    startMonth = 11; // December
    endMonth = 2; // February
    if (month === 11) seasonYear = year;
    else seasonYear = year; // Jan/Feb are part of same winter
  } else if (month >= 2 && month <= 4) { // Mar, Apr, May
    seasonIndex = 1; // Spring
    startMonth = 2;
    endMonth = 5;
  } else if (month >= 5 && month <= 7) { // Jun, Jul, Aug
    seasonIndex = 2; // Summer
    startMonth = 5;
    endMonth = 8;
  } else { // Sep, Oct, Nov
    seasonIndex = 3; // Fall
    startMonth = 8;
    endMonth = 11;
  }
  
  const startDate = new Date(startMonth === 11 ? year - (month <= 1 ? 1 : 0) : year, startMonth, 1);
  const endDate = new Date(endMonth <= 1 ? year : year, endMonth, 0);
  
  return {
    number: (year - 2024) * 4 + seasonIndex + 1, // Season 1 starts in 2024
    name: `${SEASON_NAMES[seasonIndex]} ${seasonYear}`,
    startDate,
    endDate,
    year: seasonYear
  };
}

/**
 * Get the current week number of the year
 */
export function getWeekNumber(date: Date = new Date()): number {
  const startOfYear = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + startOfYear.getDay() + 1) / 7);
}

/**
 * Get the current month key for leaderboards
 */
export function getMonthKey(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Get season reward tiers
 */
export function getSeasonRewardTiers(): RewardTier[] {
  return [
    { name: 'Grandmaster', rank: '#1', icon: '👑', coins: 1000, gems: 50, color: 'from-yellow-400 to-yellow-600' },
    { name: 'Master', rank: '#2-3', icon: '🥇', coins: 500, gems: 25, color: 'from-amber-400 to-amber-600' },
    { name: 'Diamond', rank: '#4-10', icon: '💎', coins: 250, gems: 15, color: 'from-cyan-400 to-cyan-600' },
    { name: 'Platinum', rank: '#11-25', icon: '🏆', coins: 150, gems: 10, color: 'from-slate-300 to-slate-500' },
    { name: 'Gold', rank: '#26-50', icon: '🥈', coins: 100, gems: 5, color: 'from-yellow-500 to-orange-500' },
    { name: 'Silver', rank: '#51-100', icon: '🥉', coins: 50, gems: 2, color: 'from-gray-400 to-gray-600' },
  ];
}

/**
 * Get reward tier for a given rank
 */
export function getRewardTierForRank(rank: number): RewardTier | null {
  const tiers = getSeasonRewardTiers();
  
  if (rank === 1) return tiers[0];
  if (rank <= 3) return tiers[1];
  if (rank <= 10) return tiers[2];
  if (rank <= 25) return tiers[3];
  if (rank <= 50) return tiers[4];
  if (rank <= 100) return tiers[5];
  return null;
}

/**
 * Get time remaining until season end
 */
export function getSeasonTimeRemaining(date: Date = new Date()): {
  days: number;
  hours: number;
  minutes: number;
  totalMs: number;
} {
  const season = getCurrentSeason(date);
  const totalMs = season.endDate.getTime() - date.getTime();
  
  if (totalMs <= 0) {
    return { days: 0, hours: 0, minutes: 0, totalMs: 0 };
  }
  
  const days = Math.floor(totalMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((totalMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
  
  return { days, hours, minutes, totalMs };
}

/**
 * Seeded random for consistent variance
 */
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

/**
 * Apply time-based variance to ELO for weekly/monthly snapshots
 * This makes fake player rankings feel more dynamic
 */
export function applyPeriodVariance(elo: number, playerId: string, period: LeaderboardPeriod): number {
  if (period === 'all-time' || period === 'season') {
    return elo;
  }
  
  // Create seed from player ID and current period
  const date = new Date();
  const periodSeed = period === 'weekly' 
    ? getWeekNumber(date) 
    : date.getMonth() + 1;
  
  // Generate consistent variance per player per period
  const playerHash = playerId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const seed = playerHash * periodSeed * (date.getFullYear() % 100);
  
  // Apply variance: ±50-150 ELO based on period
  const maxVariance = period === 'weekly' ? 100 : 75;
  const variance = Math.floor((seededRandom(seed) - 0.5) * 2 * maxVariance);
  
  return Math.max(400, Math.min(2800, elo + variance));
}

/**
 * Get leaderboard period display name
 */
export function getPeriodDisplayName(period: LeaderboardPeriod): string {
  switch (period) {
    case 'all-time': return 'All-Time';
    case 'season': return getCurrentSeason().name;
    case 'monthly': {
      const date = new Date();
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    case 'weekly': return `Week ${getWeekNumber()}`;
  }
}
